<template>
  <div
    class="atom-navigation-sections"
    :class="{ 'is-visible': isArrayNotEmpty(anchors) }"
  >
    <div class="atom-navigation-sections__inner">
      <div
        v-for="anchor in anchors"
        :key="anchor.id"
        class="atom-navigation-sections__item"
        :class="{ 'is-active': anchor.isActive }"
        @click="scrollToAnchor(anchor.id)"
        @keypress="scrollToAnchor(anchor.id)"
      >
        <div v-if="anchor.isActive" class="atom-navigation-sections__loader" :style="{ width: loaderWidth }" />
        <div
          class="atom-navigation-sections__bubble"
        >
          <div class="atom-navigation-sections__bubble-inner">
            {{ anchor.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const anchorStore = useAnchorStore();
const anchors = computed(() => anchorStore.getAnchors);

const scrollToAnchor = (anchorId) => {
    anchorStore.scrollToAnchor(anchorId);
};

const colorTopLevel = inject('colorSchema');
const color4 = computed(() => `var(--c-${colorTopLevel.value}-4)`);
const color2 = computed(() => `var(--c-${colorTopLevel.value}-2)`);

// Add scroll progress tracking
const loaderWidth = ref('0%');
let animationFrameId = null;

const calculateScrollProgress = () => {
    const activeAnchor = anchors.value.find((anchor) => anchor.isActive);
    if (!activeAnchor) {
        loaderWidth.value = '0%';
        animationFrameId = requestAnimationFrame(calculateScrollProgress);
        return;
    }

    const section = document.getElementById(activeAnchor.id);
    if (!section) return;

    const rect = section.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const sectionHeight = rect.height;

    // Calculate progress based on how far we've scrolled through the section
    const totalScrollDistance = sectionHeight + windowHeight;
    const scrolledDistance = windowHeight - rect.top;
    const progress = scrolledDistance / totalScrollDistance;

    loaderWidth.value = `${Math.min(100, Math.max(0, progress * 115))}%`;

    // Request next frame
    animationFrameId = requestAnimationFrame(calculateScrollProgress);
};

onMounted(() => {
    // Start the animation loop
    animationFrameId = requestAnimationFrame(calculateScrollProgress);
});

onUnmounted(() => {
    // Clean up the animation frame
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
    }
});
</script>

<style scoped lang="scss">
.atom-navigation-sections {
    @include z-index('navigationBullets');

    position: fixed;
    top: 0;
    right: 0;
    display: flex;
    height: 100%;
    align-items: flex-end;
    justify-content: center;
    transform: translateX(-100%);
    transition: transform 0.3s var(--ease-out--quart);

    @include tablet-portrait {
        display: none;
    }

    &.is-visible {
        transform: translateX(0);
    }
}

.atom-navigation-sections__inner {
    display: flex;
    flex-direction: column;
    padding-bottom: 10vh;
}

.atom-navigation-sections__item {
    @include fluid('font-size', 14px, 16px);
    @include fluid('line-height', 18px, 20px);

    display: flex;
    align-items: center;
    padding: 23px 30px;
    background-color: v-bind(color4);
    color: var(--c-white);
    cursor: pointer;
    font-family: var(--f-family--regular);
    transform: translateX(90%);
    transition: transform 0.35s var(--ease-out--quart), opacity 0.35s var(--ease-out--quart);

    &:hover,
    &.is-active {
        transform: translateX(0);
    }
}
.atom-navigation-sections__bubble-inner {
    position: relative;
    z-index: 1;
}
.atom-navigation-sections__loader {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: v-bind(color2);
}
</style>
